export const CANONICAL_ITEM_KINDS = Object.freeze({
  weapon: 'weapon',
  armor: 'armor',
  combat: 'combat',
  consumable: 'consumable',
  component: 'component',
  gear: 'gear',
  quest: 'quest',
  currency: 'currency',
  misc: 'misc',
});

export const ITEM_HOLDER_TYPES = Object.freeze({
  char: 'char',
  party: 'party',
  crafting: 'crafting',
  craftOutput: 'craftOutput',
  shop: 'shop',
  loot: 'loot',
  barter: 'barter',
});

export const ITEM_RUNTIME_ROW_TYPES = Object.freeze({
  shop: 'shop',
  loot: 'loot',
});

export const ITEM_AUTHORITY_INTENT_FIELDS = Object.freeze([
  'entryId',
  'definitionId',
  'holderType',
  'holderId',
  'quantity',
  'rulesSnapshot',
  'state',
  'provenance',
  'version',
  'idempotencyKey',
]);

export const ITEM_RULE_SNAPSHOT_KEYS = Object.freeze([
  'buffs',
  'debuffs',
  'onUseEffects',
  'combatBonuses',
  'abilities',
  'usesTemplate',
  'buffsApplyOnUse',
]);

export const ITEM_AUTHORITY_HOLDER_DESCRIPTIONS = Object.freeze({
  [ITEM_HOLDER_TYPES.char]: 'character inventory',
  [ITEM_HOLDER_TYPES.party]: 'party inventory',
  [ITEM_HOLDER_TYPES.crafting]: 'crafting table',
  [ITEM_HOLDER_TYPES.craftOutput]: 'craft output',
  [ITEM_HOLDER_TYPES.shop]: 'shop listing',
  [ITEM_HOLDER_TYPES.loot]: 'loot drop',
  [ITEM_HOLDER_TYPES.barter]: 'barter offer',
});

const KIND_SET = new Set(Object.values(CANONICAL_ITEM_KINDS));
const HOLDER_TYPE_SET = new Set(Object.values(ITEM_HOLDER_TYPES));

const cloneValue = (value) => {
  if (Array.isArray(value)) return value.map(cloneValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, innerValue]) => [key, cloneValue(innerValue)]),
    );
  }
  return value;
};

export const normalizeCanonicalItemKind = (
  value,
  fallback = CANONICAL_ITEM_KINDS.misc,
) => {
  if (KIND_SET.has(value)) return value;
  return KIND_SET.has(fallback) ? fallback : CANONICAL_ITEM_KINDS.misc;
};

export const isKnownItemHolderType = (value) => HOLDER_TYPE_SET.has(value);

export const normalizeItemHolderType = (
  value,
  fallback = ITEM_HOLDER_TYPES.char,
) => {
  if (HOLDER_TYPE_SET.has(value)) return value;
  if (fallback == null) return null;
  return HOLDER_TYPE_SET.has(fallback) ? fallback : ITEM_HOLDER_TYPES.char;
};

export const assertItemHolderType = (value) => {
  if (isKnownItemHolderType(value)) return value;
  throw new Error(`Unknown item holder type: ${value || 'missing'}`);
};

export const normalizeItemRulesSnapshot = (rules = {}) => ({
  buffs: Array.isArray(rules.buffs) ? cloneValue(rules.buffs) : [],
  debuffs: Array.isArray(rules.debuffs) ? cloneValue(rules.debuffs) : [],
  onUseEffects: Array.isArray(rules.onUseEffects) ? cloneValue(rules.onUseEffects) : [],
  combatBonuses: Array.isArray(rules.combatBonuses) ? cloneValue(rules.combatBonuses) : [],
  abilities: Array.isArray(rules.abilities) ? cloneValue(rules.abilities) : [],
  usesTemplate: rules.usesTemplate ? cloneValue(rules.usesTemplate) : null,
  buffsApplyOnUse: rules.buffsApplyOnUse === true,
});

export const buildItemAuthorityIntentContract = ({
  entryId = null,
  definitionId = null,
  holderType,
  holderId = null,
  quantity = 1,
  rulesSnapshot = {},
  state = {},
  provenance = {},
  version = null,
  idempotencyKey = null,
} = {}) => {
  const normalizedHolderType = normalizeItemHolderType(holderType, null);
  assertItemHolderType(normalizedHolderType);

  return {
    entryId: entryId == null ? null : String(entryId),
    definitionId: definitionId == null ? null : String(definitionId),
    holderType: normalizedHolderType,
    holderId: holderId == null ? null : String(holderId),
    quantity: Math.max(1, Math.trunc(Number(quantity)) || 1),
    rulesSnapshot: normalizeItemRulesSnapshot(rulesSnapshot || {}),
    state: cloneValue(state || {}),
    provenance: cloneValue(provenance || {}),
    version: version == null ? null : String(version),
    idempotencyKey: idempotencyKey == null ? null : String(idempotencyKey),
  };
};
